import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Badge } from '@/components/ui/PageHeader';
import { growth, type Campaign, type CampaignFields, type CampaignReport, type Capacity, type Purpose } from '@/api/provider-growth';
import { campaignDates } from '@/lib/provider-command-keys';
import { useGrowthCommand } from './useGrowthCommand';
import { useAuth } from '@/context/AuthContext';

const blank = (): CampaignFields => ({
  purpose: 'opening', funding: 'organic', headline: '', summary: '', body: '', terms: '',
  metroCode: '', startsAt: '', expiresAt: '',
});
function fieldsFor(value: Campaign): CampaignFields {
  return { purpose: value.purpose, funding: value.funding, headline: value.headline,
    summary: value.summary, body: value.body ?? '', terms: value.terms ?? '',
    metroCode: value.metroCode, startsAt: value.startsAt, expiresAt: value.expiresAt };
}
function localDate(value: string) {
  if (!value) return '';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
export function ProviderCampaignsPanel({ id }: { id: string }) {
  const { user } = useAuth();
  const query = useQuery({ queryKey: ['growth-campaigns', user?.id, id], queryFn: () => growth.campaigns(id), retry: false });
  const command = useGrowthCommand();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [fields, setFields] = useState<CampaignFields>(blank);
  const [capacity, setCapacity] = useState<Capacity | null>(null);
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [reason, setReason] = useState('');
  const editable = !campaign || ['draft', 'changes_requested'].includes(campaign.status);
  const dirty = !!campaign && JSON.stringify(fields) !== JSON.stringify(fieldsFor(campaign));
  function adopt(value: Campaign | null) {
    setCampaign(value); setFields(value ? fieldsFor(value) : blank());
    setCapacity(null); setReport(null); setConfirmed(false); setReason('');
  }
  function change<K extends keyof CampaignFields>(key: K, value: CampaignFields[K]) {
    setFields((old) => ({ ...old, [key]: value })); setCapacity(null); setConfirmed(false);
  }
  async function load(campaignId: string) {
    const response = await command.run('load-campaign', { id, campaignId }, () => growth.campaign(id, campaignId));
    if (response) adopt(response.data);
  }
  async function save() {
    const response = await command.run('save-campaign', { id, campaignId: campaign?.id, revision: campaign?.revision, fields },
      (idempotencyKey) => {
        const facts = { ...fields, ...campaignDates(fields.startsAt, fields.expiresAt) };
        return campaign
          ? growth.updateCampaign(id, campaign.id, facts, { idempotencyKey, expectedRevision: campaign.revision })
          : growth.createCampaign(id, facts, idempotencyKey);
      });
    if (response) { adopt(response.data); command.setMessage('Private campaign saved. Nothing is published.'); await query.refetch(); }
  }
  async function preflight() {
    const response = await command.run('campaign-capacity', { id, fields }, () =>
      growth.capacity(id, { metroCode: fields.metroCode, ...campaignDates(fields.startsAt, fields.expiresAt) }));
    if (response) setCapacity(response.data);
  }
  async function advance(action: 'submit' | 'publish' | 'retract') {
    if (!campaign || dirty) return;
    const response = await command.run(action, { id, campaignId: campaign.id, revision: campaign.revision, reason },
      (idempotencyKey) => {
        const revision = { idempotencyKey, expectedRevision: campaign.revision };
        if (action === 'submit') return growth.submitCampaign(id, campaign.id, revision);
        if (action === 'publish') return growth.publishCampaign(id, campaign.id, revision);
        return growth.retractCampaign(id, campaign.id, revision, reason);
      });
    if (response) { adopt(response.data); command.setMessage('Campaign status: ' + response.data.status); await query.refetch(); }
  }
  async function loadReport() {
    if (!campaign) return;
    const response = await command.run('campaign-report', { id, campaignId: campaign.id }, () => growth.report(id, campaign.id));
    if (response) setReport(response.data);
  }
  return <div className="space-y-4" aria-label="Reviewed Provider campaigns">
    <h3 className="font-semibold">Reviewed campaigns</h3>
    <p className="text-sm text-gray-600">Opening announcements are organic. Other promotions use free founding access. The server checks eligibility, dates and capacity. Approval does not publish a campaign or start a charge.</p>
    {query.isLoading && <p role="status">Loading campaigns…</p>}
    {query.error && <p role="alert">{query.error.message} <Button onClick={() => void query.refetch()}>Retry campaigns</Button></p>}
    <fieldset disabled={command.busy} className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        <Button variant="secondary" onClick={() => adopt(null)}>New campaign</Button>
        {(query.data?.data.campaigns ?? []).map((item) => <Button variant="secondary" key={item.id} onClick={() => void load(item.id)}>{item.headline} · {item.status}</Button>)}
      </div>
      {campaign && <p className="text-sm"><Badge variant="gray">{campaign.status}</Badge> Revision {campaign.revision} · {campaign.id}</p>}
      {campaign?.reviewMessage && <p className="text-sm">Reviewer: {campaign.reviewMessage}</p>}
      <fieldset disabled={!editable} className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm">Purpose<select className="input mt-1" value={fields.purpose} onChange={(event) => {
          const purpose = event.target.value as Purpose;
          setFields((old) => ({ ...old, purpose, funding: purpose === 'opening' ? 'organic' : 'subsidized' }));
          setCapacity(null); setConfirmed(false);
        }}>
          <option value="opening">Opening announcement</option><option value="weekly_offer">Weekly offer</option>
          <option value="seasonal_offer">Seasonal offer</option><option value="service_special">Service special</option>
          <option value="event_driven">Event promotion</option>
        </select></label>
        <p className="text-sm self-center">Funding: {fields.funding}. No payment is collected here.</p>
        <label className="text-sm">Headline<input className="input mt-1" maxLength={200} value={fields.headline} onChange={(event) => change('headline', event.target.value)} /></label>
        <label className="text-sm">Metro code<input className="input mt-1" value={fields.metroCode} onChange={(event) => change('metroCode', event.target.value)} /></label>
        {(['startsAt', 'expiresAt'] as const).map((key) => <label className="text-sm" key={key}>{key === 'startsAt' ? 'Starts (your local time)' : 'Ends (your local time)'}
          <input className="input mt-1" type="datetime-local" value={localDate(fields[key])} onChange={(event) => change(key, event.target.value ? new Date(event.target.value).toISOString() : '')} />
        </label>)}
        {(['summary', 'body', 'terms'] as const).map((key) => <label className="text-sm capitalize sm:col-span-2" key={key}>{key}<textarea className="input mt-1" rows={2} value={fields[key] ?? ''} onChange={(event) => change(key, event.target.value)} /></label>)}
      </fieldset>
      {editable && <div className="flex gap-2 flex-wrap">
        <Button variant="secondary" onClick={() => void preflight()}>Check delivery capacity</Button>
        <Button disabled={!fields.headline.trim() || !fields.summary.trim() || !fields.metroCode.trim()} onClick={() => void save()}>Save private campaign</Button>
        {campaign && <Button disabled={dirty} onClick={() => void advance('submit')}>Submit campaign for review</Button>}
      </div>}
      {dirty && <p className="text-sm">Save edits before submission. Saved campaign controls stay locked until then.</p>}
      {capacity && <p role="status" className="text-sm">Capacity: {capacity.capacityStatus} · {capacity.reasonCode}. Recent audience: {capacity.recentActiveMemberBand}. Eligible delivery target: {capacity.targetEligibleRenders}. This is an estimate, not a footfall guarantee.</p>}
      {campaign && ['approved', 'scheduled', 'live'].includes(campaign.status) && <>
        <label className="flex gap-2 text-sm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />I confirm this public-status change in the connected environment.</label>
        {campaign.status === 'approved' ? <Button disabled={!confirmed || dirty} onClick={() => void advance('publish')}>Publish approved campaign</Button> :
          <div className="space-y-2"><label className="block text-sm">Retraction reason<input className="input mt-1" maxLength={80} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
            <Button variant="danger" disabled={!confirmed || !reason.trim()} onClick={() => void advance('retract')}>Retract campaign</Button></div>}
      </>}
      {campaign && <div className="flex gap-2"><Button variant="secondary" onClick={() => void load(campaign.id)}>Reload saved campaign</Button><Button variant="secondary" onClick={() => void loadReport()}>Load delivery report</Button></div>}
    </fieldset>
    {report && <dl className="grid grid-cols-2 gap-2 text-sm">
      <dt>Eligible renders / target</dt><dd>{report.delivery.eligibleRenders} / {report.targetEligibleRenders}</dd>
      <dt>Unique members</dt><dd>{report.delivery.uniqueMembers}</dd>
      <dt>Interest / intent / outcome</dt><dd>{report.interest} / {report.intent} / {report.outcome}</dd>
      <dt>Charging enabled</dt><dd>{String(report.chargingEnabled)}</dd>
    </dl>}
    {command.error && <p role="alert" className="text-sm text-red-700">{command.error} Your edits are kept. Reload if the saved revision changed.</p>}
    {command.message && <p role="status" className="text-sm text-green-700">{command.message}</p>}
  </div>;
}
